import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Button, Popover } from "@heroui/react";
import { ROUTES } from "@/constants/routes";
import { tryNavigateWithUnsavedGuard } from "@/helpers/unsavedChangesGuard";
import "./AppNavigation.css";

export type TabId =
  | "contractors"
  | "clients"
  | "push-campaigns"
  | "feedback"
  | "gas-stations"
  | "help";

interface Tab {
  id: TabId;
  label: string;
  path: string;
}

interface AppNavigationProps {
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
}

const tabs: Tab[] = [
  { id: "contractors", label: "Контрагенты", path: ROUTES.CONTRACTORS },
  { id: "clients", label: "Клиенты", path: ROUTES.CLIENTS },
  { id: "push-campaigns", label: "Push-рассылки", path: ROUTES.PUSH_CAMPAIGNS },
  { id: "gas-stations", label: "Заправки", path: ROUTES.GAS_STATIONS },
  { id: "feedback", label: "Обратная связь", path: ROUTES.FEEDBACK },
  { id: "help", label: "Помощь на дороге", path: ROUTES.HELP },
];

const NAV_GAP_PX = 8;
const MORE_BUTTON_WIDTH_PX = 88;

const AppNavigation = ({ activeTab, onTabChange }: AppNavigationProps) => {
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [moreOpen, setMoreOpen] = useState(false);

  const handleTabClick = (tab: Tab) => {
    tryNavigateWithUnsavedGuard(() => {
      navigate(tab.path);
      onTabChange?.(tab.id);
      setMoreOpen(false);
    });
  };

  const recalcVisible = useCallback(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure) return;

    const buttons = measure.querySelectorAll<HTMLButtonElement>(
      "[data-measure-tab]",
    );
    const widths = Array.from(buttons).map((btn) => btn.offsetWidth);
    const available = nav.clientWidth;

    if (available <= 0 || widths.length === 0) return;

    const totalWidth = (count: number, withMore: boolean) => {
      let sum = 0;
      for (let i = 0; i < count; i++) {
        sum += widths[i] + NAV_GAP_PX;
      }
      if (withMore) sum += MORE_BUTTON_WIDTH_PX + NAV_GAP_PX;
      return sum;
    };

    let count = tabs.length;
    while (count > 0 && totalWidth(count, count < tabs.length) > available) {
      count -= 1;
    }

    setVisibleCount(Math.max(1, count));
  }, [activeTab]);

  useLayoutEffect(() => {
    recalcVisible();
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => recalcVisible());
    ro.observe(nav);
    return () => ro.disconnect();
  }, [recalcVisible]);

  let visibleTabs = tabs.slice(0, visibleCount);
  let overflowTabs = tabs.slice(visibleCount);
  if (
    activeTab &&
    overflowTabs.some((t) => t.id === activeTab) &&
    visibleTabs.length > 0
  ) {
    const active = tabs.find((t) => t.id === activeTab);
    const replaced = visibleTabs[visibleTabs.length - 1];
    if (active && replaced) {
      visibleTabs = [...visibleTabs.slice(0, -1), active];
      overflowTabs = [
        replaced,
        ...overflowTabs.filter((t) => t.id !== activeTab),
      ];
    }
  }
  const overflowActive = overflowTabs.some((t) => t.id === activeTab);

  const renderTabButton = (tab: Tab, measure = false) => {
    const tabClassName = classNames("app-navigation__tab", {
      "app-navigation__tab_active": activeTab === tab.id,
    });

    return (
      <button
        key={tab.id}
        type="button"
        className={tabClassName}
        data-measure-tab={measure ? "" : undefined}
        onClick={measure ? undefined : () => handleTabClick(tab)}
        data-testid={
          tab.id === "push-campaigns"
            ? "nav-push-campaigns"
            : `nav-${tab.id}`
        }
      >
        {tab.label}
      </button>
    );
  };

  return (
    <>
      <div ref={measureRef} className="app-navigation__measure" aria-hidden>
        {tabs.map((tab) => renderTabButton(tab, true))}
      </div>
      <nav ref={navRef} className="app-navigation" data-testid="nav">
        {visibleTabs.map((tab) => renderTabButton(tab))}
        {overflowTabs.length > 0 && (
          <Popover isOpen={moreOpen} onOpenChange={setMoreOpen}>
            <Popover.Trigger>
              <button
                type="button"
                className={classNames("app-navigation__tab app-navigation__more", {
                  "app-navigation__tab_active": overflowActive,
                })}
                data-testid="nav-more"
              >
                Ещё
              </button>
            </Popover.Trigger>
            <Popover.Content placement="bottom end" className="app-navigation__more-popover">
              <Popover.Dialog className="app-navigation__more-menu">
                {overflowTabs.map((tab) => (
                  <Button
                    key={tab.id}
                    variant="ghost"
                    className={classNames("app-navigation__more-item", {
                      "app-navigation__more-item_active": activeTab === tab.id,
                    })}
                    data-testid={`nav-${tab.id}`}
                    onPress={() => handleTabClick(tab)}
                  >
                    {tab.label}
                  </Button>
                ))}
              </Popover.Dialog>
            </Popover.Content>
          </Popover>
        )}
      </nav>
    </>
  );
};

export default AppNavigation;
